// Convert a gs:// URI to its public HTTPS form.
export const gcsUriToHttp = (uri) => {
    if (!uri || typeof uri !== 'string') {
        return null
    }
    if (uri.startsWith('https://')) {
        return uri
    }
    if (!uri.startsWith('gs://')) {
        return null
    }
    const rest = uri.slice('gs://'.length)
    const slash = rest.indexOf('/')
    if (slash < 0) {
        return null
    }
    return `https://storage.googleapis.com/${rest.slice(0, slash)}/${rest.slice(slash + 1)}`
}


// Deterministic palette per brand name — same shape as the mock's avatarColor.
const PALETTE = [
    { color: '#C7A583', gradient: 'linear-gradient(135deg, #F3E3C8 0%, #D9C2A0 100%)' },
    { color: '#4F7FB2', gradient: 'linear-gradient(135deg, #C9D9EC 0%, #6E97C2 100%)' },
    { color: '#5E8267', gradient: 'linear-gradient(135deg, #DCE7DD 0%, #7A9D85 100%)' },
    { color: '#13343B', gradient: 'linear-gradient(135deg, #B7C7CB 0%, #2E5159 100%)' },
    { color: '#8F7DBA', gradient: 'linear-gradient(135deg, #DCD3EE 0%, #A696CC 100%)' },
    { color: '#C94844', gradient: 'linear-gradient(135deg, #F2C7C6 0%, #DA6E6B 100%)' },
    { color: '#C2873E', gradient: 'linear-gradient(135deg, #F3E3C8 0%, #D9A064 100%)' },
    { color: '#7E9398', gradient: 'linear-gradient(135deg, #DDE5E7 0%, #93AAB0 100%)' },
]

const hashName = (name) => {
    let h = 0
    for (let i = 0; i < name.length; i++) {
        h = (h * 31 + name.charCodeAt(i)) >>> 0
    }
    return h
}

export const brandPaletteFromName = (name) => {
    const seed = hashName(name ?? '')
    return PALETTE[seed % PALETTE.length]
}


// Monogram = first 2 letters, uppercase
export const monogramFromName = (name) => {
    if (!name) {
        return '—'
    }
    const cleaned = name.replace(/[^A-Za-z]/g, '')
    return cleaned.slice(0, 2).toUpperCase()
}


// Sentinel tokens that should never reach the UI. The engine's analysis
// passes occasionally emit these as literal strings ("null", "none", …)
// where a value is simply absent — treat them all as "no value".
const JUNK_VALUES = new Set(['', '-', '—', 'null', 'none', 'n/a', 'na', 'undefined', 'nil'])


// Normalize a value for display: trims strings and collapses junk sentinels
// (nullish, blank, or tokens like "null"/"none") to `null` so callers can
// guard on the result and omit the element entirely instead of rendering the
// literal token. Non-string values pass through unchanged (nullish → null).
export const clean = (v) => {
    if (typeof v !== 'string') {
        return v ?? null
    }
    const trimmed = v.trim()
    if (JUNK_VALUES.has(trimmed.toLowerCase())) {
        return null
    }
    return trimmed
}


// Humanize snake_case / kebab-case values into readable sentence case.
// Junk sentinels ("null", "none", blank, …) collapse to `null` so they never
// render — guard on the return value to drop the surrounding element.
//
// Rules:
//   - junk sentinel / nullish → null
//   - already has whitespace → pass through (natural language)
//   - contains _ or - → replace separators, lowercase, capitalize first
//   - otherwise → pass through unchanged
//
// Examples:
//   "dermatologist_authority"        → "Dermatologist authority"
//   "before-and-after"               → "Before and after"
//   "Deliver_clinical_results"       → "Deliver clinical results"
//   "Hello, world."                  → "Hello, world." (has space, untouched)
//   "singleword"                     → "singleword"   (no separator, untouched)
//   "null" / "none" / null           → null
export const humanize = (s) => {
    const c = clean(s)
    if (typeof c !== 'string' || !c) {
        return c
    }
    if (/\s/.test(c)) {
        return c
    }
    if (!/[_-]/.test(c)) {
        return c
    }
    const spaced = c.replace(/[_-]+/g, ' ').toLowerCase()
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
